import { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { Truck, Store, Plus, Trash2, MapPin, X } from 'lucide-react'
import { shippingAPI } from '../../services/api'
import { useTheme } from '../../contexts/ThemeContext'
import { t, getLanguage } from '../../i18n'

const COUNTRIES = {
  OM: { name: 'Oman', flag: '🇴🇲' },
  SA: { name: 'Saudi Arabia', flag: '🇸🇦' },
  AE: { name: 'United Arab Emirates', flag: '🇦🇪' },
  QA: { name: 'Qatar', flag: '🇶🇦' },
  KW: { name: 'Kuwait', flag: '🇰🇼' },
  BH: { name: 'Bahrain', flag: '🇧🇭' },
}
const countryName = code => (COUNTRIES[code] ? `${COUNTRIES[code].flag} ${t(COUNTRIES[code].name)}` : t('Other areas'))

function Switch({ on, onChange, label, disabled }) {
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={label} disabled={disabled} onClick={() => onChange(!on)}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${on ? 'bg-blue-600' : 'bg-gray-300'}`}>
      <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${on ? 'translate-x-5 rtl:-translate-x-5' : 'translate-x-0.5 rtl:-translate-x-0.5'}`} />
    </button>
  )
}

// A price field that saves when the owner leaves it.
function PriceInput({ value, onSave, placeholder, label }) {
  const [draft, setDraft] = useState(value ?? '')
  useEffect(() => setDraft(value ?? ''), [value])
  const commit = () => {
    const current = value == null ? '' : String(Number(value))
    const next = draft === '' ? '' : String(Number(draft))
    if (next !== current) onSave(draft === '' ? null : draft)
  }
  return (
    <input type="number" min="0" step="0.01" inputMode="decimal" aria-label={label} placeholder={placeholder}
      value={draft} onChange={e => setDraft(e.target.value)} onBlur={commit} onKeyDown={e => e.key === 'Enter' && e.currentTarget.blur()}
      className="w-24 rounded-lg border border-gray-300 px-2 py-1.5 text-sm tabular-nums focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
  )
}

const EMPTY_FORM = { country: 'OM', region: '', name: '', name_ar: '', price: '', free_over: '', eta: '' }

function AddAreaForm({ onClose, onAdded, currency }) {
  const [form, setForm] = useState(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const set = key => e => setForm(f => ({ ...f, [key]: e.target.value }))
  const submit = async e => {
    e.preventDefault()
    setSaving(true)
    try {
      await shippingAPI.create({ ...form, country: form.country || null })
      toast.success(t('Delivery area added'))
      setForm(f => ({ ...EMPTY_FORM, country: f.country, region: f.region }))
      onAdded()
    } catch (err) {
      toast.error(t(err.response?.data?.error || 'Failed to add the delivery area'))
    } finally { setSaving(false) }
  }
  const field = 'mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500'
  return (
    <form onSubmit={submit} className="rounded-xl border border-blue-200 bg-blue-50/40 p-4 sm:p-5">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="font-semibold text-gray-900">{t('Add a delivery area')}</h3>
        <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-gray-500 hover:bg-white" aria-label={t('Close')}><X className="h-5 w-5" /></button>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <label className="text-sm font-medium text-gray-700">{t('Country')}
          <select value={form.country} onChange={set('country')} className={field}>
            {Object.keys(COUNTRIES).map(code => <option key={code} value={code}>{countryName(code)}</option>)}
            <option value="">{t('Other areas')}</option>
          </select>
        </label>
        <label className="text-sm font-medium text-gray-700">{t('Region / governorate')}
          <input value={form.region} onChange={set('region')} maxLength={100} placeholder={t('e.g. Muscat')} className={field} />
        </label>
        <label className="text-sm font-medium text-gray-700">{t('City or area')} *
          <input value={form.name} onChange={set('name')} required maxLength={120} placeholder={t('e.g. Al Khuwair')} className={field} />
        </label>
        <label className="text-sm font-medium text-gray-700">{t('Name in Arabic')}
          <input value={form.name_ar} onChange={set('name_ar')} maxLength={120} dir="rtl" placeholder="الخوير" className={field} />
        </label>
        <label className="text-sm font-medium text-gray-700">{t('Delivery price')} ({currency}) *
          <input type="number" min="0" step="0.01" required value={form.price} onChange={set('price')} placeholder="0" className={field} />
        </label>
        <label className="text-sm font-medium text-gray-700">{t('Free delivery from')} ({currency})
          <input type="number" min="0" step="0.01" value={form.free_over} onChange={set('free_over')} placeholder={t('Optional')} className={field} />
        </label>
        <label className="text-sm font-medium text-gray-700 sm:col-span-2 lg:col-span-1">{t('Delivery time')}
          <input value={form.eta} onChange={set('eta')} maxLength={60} placeholder={t('e.g. Same day')} className={field} />
        </label>
      </div>
      <div className="mt-4 flex justify-end">
        <button disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
          <Plus className="h-4 w-4" />{saving ? t('Saving...') : t('Add area')}
        </button>
      </div>
    </form>
  )
}

function QuickStart({ onDone, currency, existing }) {
  const [price, setPrice] = useState('')
  const [busy, setBusy] = useState(null)
  const add = async country => {
    setBusy(country)
    try {
      const { data } = await shippingAPI.addPreset(country, price === '' ? 0 : price)
      toast.success(data.added ? t('{v0} cities added', { v0: data.added }) : t('These cities are already in your list'))
      onDone()
    } catch (err) {
      toast.error(t(err.response?.data?.error || 'Failed to add the cities'))
    } finally { setBusy(null) }
  }
  return (
    <section className="rounded-xl border border-gray-200 bg-white p-4 sm:p-5">
      <h2 className="font-semibold text-gray-900">{existing ? t('Add more cities') : t('Start with a ready list of cities')}</h2>
      <p className="mt-1 text-sm text-gray-500">{t('Adds the main cities with one price. You can change each price afterwards, or switch off the ones you don’t deliver to.')}</p>
      <div className="mt-4 flex flex-wrap items-end gap-3">
        <label className="text-sm font-medium text-gray-700">{t('Starting price')} ({currency})
          <input type="number" min="0" step="0.01" value={price} onChange={e => setPrice(e.target.value)} placeholder="0"
            className="mt-1 block w-28 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
        </label>
        {['OM', 'SA'].map(code => (
          <button key={code} type="button" disabled={!!busy} onClick={() => add(code)}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50">
            <span aria-hidden="true">{COUNTRIES[code].flag}</span>
            {busy === code ? t('Adding...') : t(code === 'OM' ? 'Add Oman cities' : 'Add Saudi cities')}
          </button>
        ))}
      </div>
    </section>
  )
}

export default function ShippingPage() {
  const { getCurrency } = useTheme()
  const currency = getCurrency()
  const [data, setData] = useState(null)
  const [adding, setAdding] = useState(false)
  const [regionPrice, setRegionPrice] = useState({})
  const arabic = getLanguage() === 'ar'

  const load = () => shippingAPI.list().then(r => setData(r.data)).catch(err => toast.error(t(err.response?.data?.error || 'Failed to load delivery areas')))
  useEffect(() => { load() }, [])

  // Country -> region -> areas, in the order the store keeps them.
  const groups = useMemo(() => {
    const byCountry = new Map()
    for (const zone of data?.zones || []) {
      const country = zone.country || ''
      if (!byCountry.has(country)) byCountry.set(country, new Map())
      const regions = byCountry.get(country)
      const region = zone.region || ''
      if (!regions.has(region)) regions.set(region, [])
      regions.get(region).push(zone)
    }
    return byCountry
  }, [data])

  const saveOption = async (key, value) => {
    const previous = data
    setData(d => ({ ...d, [key]: value }))
    try { await shippingAPI.saveSettings({ [key]: value }) }
    catch (err) { setData(previous); toast.error(t(err.response?.data?.error || 'Failed to save')) }
  }

  const updateZone = async (zone, patch) => {
    setData(d => ({ ...d, zones: d.zones.map(z => (z.id === zone.id ? { ...z, ...patch } : z)) }))
    try { await shippingAPI.update(zone.id, patch) }
    catch (err) { toast.error(t(err.response?.data?.error || 'Failed to update the delivery area')); load() }
  }

  const removeZone = async zone => {
    if (!window.confirm(t('Remove {v0} from your delivery areas?', { v0: zone.name }))) return
    try { await shippingAPI.remove(zone.id); setData(d => ({ ...d, zones: d.zones.filter(z => z.id !== zone.id) })) }
    catch (err) { toast.error(t(err.response?.data?.error || 'Failed to delete the delivery area')) }
  }

  const applyRegionPrice = async (country, region) => {
    const key = `${country}|${region}`
    const price = regionPrice[key]
    if (price === undefined || price === '') return
    try {
      const { data: r } = await shippingAPI.bulkPrice({ country: country || undefined, region: region || undefined, price })
      toast.success(t('Price updated for {v0} areas', { v0: r.updated }))
      setRegionPrice(p => ({ ...p, [key]: '' }))
      load()
    } catch (err) { toast.error(t(err.response?.data?.error || 'Failed to update prices')) }
  }

  if (!data) return <div className="p-6 text-center text-gray-400">{t('Loading...')}</div>
  const hasZones = data.zones.length > 0

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('Shipping & delivery')}</h1>
        <p className="mt-1 text-sm text-gray-500">{t('Choose where you deliver and how much it costs. Customers pick their area at checkout and the price is added to their order.')}</p>
      </div>

      <section className="divide-y divide-gray-100 rounded-xl border border-gray-200 bg-white">
        {[['delivery_enabled', Truck, 'Delivery', 'Customers enter their address and choose their area.'],
          ['pickup_enabled', Store, 'Pickup', 'Customers collect their order from you.']].map(([key, Icon, label, hint]) => (
          <div key={key} className="flex items-center gap-4 p-4 sm:px-5">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-700"><Icon className="h-5 w-5" /></span>
            <div className="min-w-0 flex-1">
              <p className="font-medium text-gray-900">{t(label)}</p>
              <p className="text-sm text-gray-500">{t(hint)}</p>
            </div>
            <Switch on={data[key]} label={t(label)} onChange={v => saveOption(key, v)} />
          </div>
        ))}
      </section>

      {data.delivery_enabled && (
        <>
          <QuickStart onDone={load} currency={currency} existing={hasZones} />

          <section className="rounded-xl border border-gray-200 bg-white">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 p-4 sm:px-5">
              <div>
                <h2 className="font-semibold text-gray-900">{t('Delivery areas')}</h2>
                <p className="text-sm text-gray-500">{hasZones
                  ? t('{v0} areas · customers only see the ones switched on', { v0: data.zones.length })
                  : t('No areas yet - delivery is free and you arrange it with the customer.')}</p>
              </div>
              {!adding && (
                <button onClick={() => setAdding(true)} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">
                  <Plus className="h-4 w-4" />{t('Add area manually')}
                </button>
              )}
            </div>

            {adding && <div className="p-4 sm:p-5"><AddAreaForm currency={currency} onClose={() => setAdding(false)} onAdded={load} /></div>}

            {!hasZones && !adding && (
              <div className="px-5 py-10 text-center">
                <MapPin className="mx-auto h-10 w-10 text-gray-300" />
                <p className="mt-2 text-sm text-gray-500">{t('Add the cities you deliver to with the buttons above, or type your own areas.')}</p>
              </div>
            )}

            {[...groups.entries()].map(([country, regions]) => (
              <div key={country || 'other'} className="border-t border-gray-100 first:border-t-0">
                <h3 className="bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-700 sm:px-5">{countryName(country)}</h3>
                {[...regions.entries()].map(([region, zones]) => {
                  const key = `${country}|${region}`
                  return (
                    <div key={key} className="border-t border-gray-100">
                      <div className="flex flex-wrap items-center justify-between gap-2 px-4 pt-3 sm:px-5">
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{region || t('No region')}</p>
                        <div className="flex items-center gap-2">
                          <input type="number" min="0" step="0.01" inputMode="decimal" aria-label={t('Price for the whole region')} placeholder={t('All')}
                            value={regionPrice[key] ?? ''} onChange={e => setRegionPrice(p => ({ ...p, [key]: e.target.value }))}
                            onKeyDown={e => e.key === 'Enter' && applyRegionPrice(country, region)}
                            className="w-20 rounded-lg border border-gray-300 px-2 py-1.5 text-sm tabular-nums focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                          <button type="button" onClick={() => applyRegionPrice(country, region)} disabled={!regionPrice[key]}
                            className="rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40">
                            {t('Set for all')}
                          </button>
                        </div>
                      </div>
                      <ul className="divide-y divide-gray-50">
                        {zones.map(zone => (
                          <li key={zone.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2.5 sm:px-5">
                            <div className="min-w-[9rem] flex-1">
                              <p className={`text-sm font-medium ${zone.is_active ? 'text-gray-900' : 'text-gray-400'}`}>{arabic && zone.name_ar ? zone.name_ar : zone.name}</p>
                              {(zone.eta || (zone.name_ar && !arabic)) && <p className="text-xs text-gray-500">{[!arabic && zone.name_ar, zone.eta].filter(Boolean).join(' · ')}</p>}
                            </div>
                            <label className="flex items-center gap-1.5 text-xs text-gray-500">
                              <span className="sr-only sm:not-sr-only">{t('Price')}</span>
                              <PriceInput value={zone.price} label={t('Delivery price')} onSave={v => v != null && updateZone(zone, { price: v })} />
                              <span>{currency}</span>
                            </label>
                            <label className="flex items-center gap-1.5 text-xs text-gray-500">
                              <span>{t('Free from')}</span>
                              <PriceInput value={zone.free_over} label={t('Free delivery from')} placeholder="—" onSave={v => updateZone(zone, { free_over: v })} />
                            </label>
                            <div className="ms-auto flex items-center gap-2">
                              <Switch on={!!zone.is_active} label={t('Deliver to {v0}', { v0: zone.name })} onChange={v => updateZone(zone, { is_active: v })} />
                              <button type="button" onClick={() => removeZone(zone)} className="rounded-lg p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600" aria-label={t('Remove {v0}', { v0: zone.name })}>
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )
                })}
              </div>
            ))}
          </section>
        </>
      )}
    </div>
  )
}
